import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../components/Layout';
import { useVisibleInitiatives } from '../hooks/useScopedData';
import Drawer from '../components/Drawer';
import RefBadge from '../components/RefBadge';
import {
  buildMonthGrid,
  initiativesByCalendarDate,
  initiativesForCalendar,
  weekdayLabels,
} from '../lib/calendarGrid';
import { recName, type Initiative } from '../types';

type CalEntry = { data: Initiative };

// A stable color per committee (by its position in settings.committees), plus
// a muted fallback for initiatives with no committee. Cycles if there are
// ever more committees than colors.
const PALETTE = [
  { bg: '#eaf3fb', fg: '#1b5b8c' }, // blue
  { bg: '#eafaf0', fg: '#1f7a4d' }, // green
  { bg: '#fdf1e6', fg: '#9a5a12' }, // orange
  { bg: '#f5eefc', fg: '#6b3fa0' }, // purple
  { bg: '#fdecee', fg: '#a33b4a' }, // red
  { bg: '#eef7f7', fg: '#1f7a7a' }, // teal
];
const FALLBACK_COLOR = { bg: '#eef2f6', fg: '#5b6f82' };

function committeeColor(committeeId: string | undefined, committees: { id: string }[]) {
  const i = committeeId ? committees.findIndex((c) => c.id === committeeId) : -1;
  return i < 0 ? FALLBACK_COLOR : PALETTE[i % PALETTE.length];
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CalendarView() {
  const { profile, settings, districtFilter } = useAppContext();
  const initiativesQ = useVisibleInitiatives(profile);

  const now = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [openEntry, setOpenEntry] = useState<CalEntry | null>(null);

  const grid = useMemo(
    () => buildMonthGrid(cursor.year, cursor.month, todayISO()),
    [cursor],
  );

  const initiatives = useMemo(() => {
    return initiativesForCalendar(initiativesQ.data, districtFilter);
  }, [initiativesQ.data, districtFilter]);

  const byDay = useMemo(() => initiativesByCalendarDate(initiatives), [initiatives]);

  // Scoped to the month actually on screen (not every initiative ever),
  // so the legend only lists colors that appear somewhere in the grid.
  const legend = useMemo(() => {
    const committeesUsed = new Set<string>();
    for (const day of grid.days) {
      for (const entry of byDay.get(day.iso) ?? []) {
        if (entry.committee) committeesUsed.add(entry.committee);
      }
    }
    return settings.committees.filter((c) => committeesUsed.has(c.id));
  }, [grid, byDay, settings.committees]);

  const goPrev = () =>
    setCursor((c) => (c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 }));
  const goNext = () =>
    setCursor((c) => (c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 }));
  const goToday = () => setCursor({ year: now.getFullYear(), month: now.getMonth() });

  return (
    <>
      <section className="iv-intro">
        <div>
          <h2>Calendar</h2>
          <p>Dated Compass Initiatives, at a glance. Use the district picker above to focus the view.</p>
        </div>
      </section>

      <div className="card cal-card">
        <div className="cal-toolbar">
          <div className="cal-nav">
            <button className="btn" onClick={goPrev} aria-label="Previous month">
              ‹
            </button>
            <button className="btn" onClick={goToday}>
              Today
            </button>
            <button className="btn" onClick={goNext} aria-label="Next month">
              ›
            </button>
          </div>
          <h3 className="cal-title">{grid.label}</h3>
          <div className="cal-legend">
            {legend.map((c) => {
              const color = committeeColor(c.id, settings.committees);
              return (
                <span key={c.id} className="cal-legend-item">
                  <span className="cal-swatch" style={{ background: color.fg }} />
                  {c.name}
                </span>
              );
            })}
            {legend.length === 0 && (
              <span className="cal-legend-item subtitle">No dated Initiatives this month</span>
            )}
          </div>
        </div>

        <div className="cal-grid cal-weekdays">
          {weekdayLabels().map((w) => (
            <div key={w} className="cal-weekday">
              {w}
            </div>
          ))}
        </div>
        <div className="cal-grid">
          {grid.days.map((day) => {
            const dayEntries = (byDay.get(day.iso) ?? []).map((data) => ({ data }));
            const shown = dayEntries.slice(0, 3);
            return (
              <div
                key={day.iso}
                className={`cal-cell${day.inMonth ? '' : ' is-out'}${day.isToday ? ' is-today' : ''}`}
              >
                <div className="cal-daynum">{day.day}</div>
                <div className="cal-events">
                  {shown.map((entry) => {
                    const init = entry.data;
                    const color = committeeColor(init.committee, settings.committees);
                    return (
                      <button
                        key={init.id}
                        type="button"
                        className="cal-event"
                        style={{ background: color.bg, color: color.fg }}
                        onClick={() => setOpenEntry(entry)}
                        title={init.name}
                      >
                        {districtFilter === 'All' && (
                          <span className="cal-event-district">
                            {recName(settings.districts, init.districtId)}
                          </span>
                        )}
                        <span>{init.name}</span>
                      </button>
                    );
                  })}
                  {dayEntries.length > shown.length && (
                    <div className="cal-more">+{dayEntries.length - shown.length} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {openEntry && (
        <Drawer
          title={openEntry.data.name}
          subtitle={<RefBadge value={openEntry.data.ref} />}
          onClose={() => setOpenEntry(null)}
        >
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
            {recName(settings.districts, openEntry.data.districtId)} ·{' '}
            {recName(settings.committees, openEntry.data.committee)}
          </div>
          <div style={{ fontSize: 14, marginBottom: 6 }}>
            <b>Date:</b> {openEntry.data.eventDate}
            {openEntry.data.eventEndDate ? ` – ${openEntry.data.eventEndDate}` : ''}
            {openEntry.data.backupDate ? ` (backup ${openEntry.data.backupDate})` : ''}
          </div>
          <div style={{ fontSize: 14, marginBottom: 16, textTransform: 'capitalize' }}>
            <b style={{ textTransform: 'none' }}>Status:</b> {openEntry.data.status}
          </div>
          <Link
            className="btn primary"
            to="/initiatives"
            state={{ focusInitiativeId: openEntry.data.id }}
            onClick={() => setOpenEntry(null)}
          >
            Open in Initiatives →
          </Link>
        </Drawer>
      )}
    </>
  );
}
